import { useCallback, useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { BookOpen, Plus, Pencil, Trash2, Search, Clock, Beaker, Tag, Syringe, Zap, Star } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import LogShotDialog from "../components/LogShotDialog";
import ReconstitutionGuide from "../components/ReconstitutionGuide";
import InfoTip from "../components/InfoTip";
import MentorshipStrip from "../components/MentorshipStrip";
import PageHero from "../components/PageHero";

const emptyForm = { name: "", category: "", description: "", default_dose: "", default_unit: "mcg", default_interval_hours: "24", suggested_vial_mg: "", suggested_bac_water_ml: "" };
const ALL = "All Products";

export default function Library() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [peptides, setPeptides] = useState([]);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState(ALL);
  const [selected, setSelected] = useState(null); // peptide detail dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [shots, setShots] = useState([]);
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [quickLogPeptide, setQuickLogPeptide] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/peptides");
      setPeptides(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);
  const loadShots = useCallback(async () => {
    try {
      const { data } = await api.get("/shots");
      setShots(data);
    } catch (err) {
      // shots aren't strictly needed here; log for debugging but don't surface
      console.debug("[Library] shots fetch failed:", err?.message || err);
    }
  }, []);
  useEffect(() => { load(); loadShots(); }, [load, loadShots]);

  const openQuickLog = (p) => {
    setQuickLogPeptide(p.name);
    setSelected(null);
    setLogDialogOpen(true);
  };

  // Build unique category list (sorted, "All Products" first)
  const categories = useMemo(() => {
    const set = new Set();
    peptides.forEach((p) => { if (p.category) set.add(p.category); });
    return [ALL, ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [peptides]);

  // Filter by search + active category
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return peptides.filter((p) => {
      if (activeCategory !== ALL && (p.category || "") !== activeCategory) return false;
      if (!q) return true;
      return (
        (p.name || "").toLowerCase().includes(q) ||
        (p.category || "").toLowerCase().includes(q) ||
        (p.description || "").toLowerCase().includes(q)
      );
    });
  }, [peptides, search, activeCategory]);

  const categoryCount = (cat) => cat === ALL ? peptides.length : peptides.filter((p) => p.category === cat).length;

  // "Your Top 5" — peptides the user has logged most often. Peptide match by name (case-insensitive).
  const topFive = useMemo(() => {
    if (!shots?.length || !peptides?.length) return [];
    const counts = new Map();
    for (const s of shots) {
      const key = (s.peptide_name || "").trim().toLowerCase();
      if (!key) continue;
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    return peptides
      .map((p) => ({ p, count: counts.get((p.name || "").toLowerCase()) || 0 }))
      .filter((x) => x.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [shots, peptides]);

  const openNew = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      category: p.category || "",
      description: p.description || "",
      default_dose: p.default_dose ?? "",
      default_unit: p.default_unit || "mcg",
      default_interval_hours: p.default_interval_hours ?? "24",
      suggested_vial_mg: p.suggested_vial_mg ?? "",
      suggested_bac_water_ml: p.suggested_bac_water_ml ?? "",
    });
    setDialogOpen(true);
    setSelected(null);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category || null,
        description: form.description || null,
        default_dose: form.default_dose === "" ? null : parseFloat(form.default_dose),
        default_unit: form.default_unit || null,
        default_interval_hours: form.default_interval_hours === "" ? 24 : parseFloat(form.default_interval_hours),
        suggested_vial_mg: form.suggested_vial_mg === "" ? null : parseFloat(form.suggested_vial_mg),
        suggested_bac_water_ml: form.suggested_bac_water_ml === "" ? null : parseFloat(form.suggested_bac_water_ml),
      };
      if (editing) {
        await api.put(`/peptides/${editing.id}`, payload);
        toast.success("Peptide updated");
      } else {
        await api.post("/peptides", payload);
        toast.success("Peptide added");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/peptides/${confirmDelete.id}`);
      toast.success("Peptide deleted");
      setConfirmDelete(null);
      setSelected(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <AppLayout>
      <PageHero
        eyebrow="Reference"
        title="Library"
        subtitle={`${peptides.length} peptides available. ${isAdmin ? "Admin: manage the shared library." : "Tap any peptide for details."}`}
        icon={<BookOpen className="w-8 h-8 text-[#0D9488]" />}
        gradient
        actions={
          isAdmin ? (
            <Button onClick={openNew}
              className="bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none h-11 px-6 uppercase tracking-widest font-semibold"
              data-testid="add-peptide-button">
              <Plus className="w-4 h-4 mr-2" /> Add Peptide
            </Button>
          ) : null
        }
      />

      {/* Search */}
      <div className="mb-4 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, category, or description…"
          className="pl-10 bg-slate-50 border-slate-200 rounded-none h-11" data-testid="peptide-search" />
      </div>

      <MentorshipStrip topic="peptides" label="Peptide Mentorship" />

      {/* Category picker
          - Mobile (<sm): native <select> dropdown — chips overflow on narrow phones
          - Desktop (sm+): the original chip row */}
      <div className="mb-6" data-testid="library-category-picker">
        {/* Mobile dropdown */}
        <div className="sm:hidden">
          <label className="block font-mono-num text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">
            // Filter by category
          </label>
          <select
            value={activeCategory}
            onChange={(e) => setActiveCategory(e.target.value)}
            className="w-full h-11 rounded-none border border-slate-200 bg-white text-slate-900 text-sm px-3 focus:outline-none focus:border-[#0D9488]"
            data-testid="library-category-select"
          >
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat} ({categoryCount(cat)})
              </option>
            ))}
          </select>
        </div>

        {/* Desktop pills */}
        <div className="hidden sm:flex flex-wrap gap-2" data-testid="library-category-pills">
          {categories.map((cat) => {
            const active = cat === activeCategory;
            const count = categoryCount(cat);
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveCategory(cat)}
                className={`inline-flex items-center gap-2 h-9 px-4 rounded-full text-sm font-medium border transition-colors ${
                  active
                    ? "bg-[#0D9488] hover:bg-[#0F766E] text-black border-[#0D9488]"
                    : "bg-slate-50/70 hover:bg-slate-200 text-slate-800 border-slate-200"
                }`}
                data-testid={`library-cat-pill-${cat.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <span>{cat}</span>
                <span className={`font-mono-num text-[10px] px-1.5 py-0.5 ${active ? "bg-black/20 text-black" : "bg-slate-50 text-slate-500"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Your Top 5 — pinned quick-log row (only shows once the user has logged shots) */}
      {topFive.length > 0 && (
        <div className="mb-6" data-testid="library-top-five">
          <div className="flex items-center gap-2 mb-2">
            <Star className="w-3.5 h-3.5 text-[#0D9488]" />
            <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-[#0D9488]">// Your Top 5</div>
            <div className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-500">most-logged peptides</div>
          </div>
          <div className="flex flex-wrap gap-2">
            {topFive.map(({ p, count }) => (
              <div
                key={p.id}
                role="button"
                tabIndex={0}
                onClick={() => setSelected(p)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected(p); } }}
                className="group flex items-center gap-2 bg-white border border-slate-200 hover:border-[#0D9488] pl-3 pr-1 py-1 cursor-pointer transition-colors"
                data-testid={`library-top5-card-${p.id}`}
              >
                <div className="flex flex-col leading-tight">
                  <span className="font-display font-black text-sm uppercase tracking-tight text-slate-900 group-hover:text-[#0D9488] transition-colors">
                    {p.name}
                  </span>
                  <span className="font-mono-num text-[9px] uppercase tracking-widest text-slate-500">
                    {count} shot{count === 1 ? "" : "s"}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); openQuickLog(p); }}
                  className="ml-1 inline-flex items-center gap-1 bg-[#0D9488] hover:bg-[#0F766E] text-black font-mono-num text-[10px] uppercase tracking-widest font-black px-2 h-7"
                  data-testid={`library-top5-quicklog-${p.id}`}
                  title="Log this peptide now"
                >
                  <Zap className="w-3 h-3" /> Log
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Peptide grid */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 p-16 text-center">
          <div className="font-display font-black text-2xl uppercase text-slate-500">Nothing here yet</div>
          <p className="text-slate-500 mt-2 text-sm">Try a different category or search term.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="library-grid">
          {filtered.map((p) => (
            <div
              key={p.id}
              role="button"
              tabIndex={0}
              onClick={() => setSelected(p)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected(p); } }}
              className="text-left bg-white border border-slate-200 hover:border-[#0D9488] hover:bg-slate-50/60 p-4 transition-colors group relative cursor-pointer focus:outline-none focus:border-[#0D9488]"
              data-testid={`library-peptide-card-${p.id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                  <div className="font-display font-black text-lg uppercase tracking-tight text-slate-900 truncate group-hover:text-[#0D9488] transition-colors">
                    {p.name}
                  </div>
                  {p.category && (
                    <div className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1 flex items-center gap-1">
                      <Tag className="w-2.5 h-2.5" /> {p.category}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); openQuickLog(p); }}
                  className="shrink-0 inline-flex items-center gap-1 bg-transparent border border-slate-200 group-hover:border-[#0D9488] group-hover:bg-[#0D9488] group-hover:text-black hover:!bg-[#0D9488] hover:!text-black text-[#0D9488] font-mono-num text-[10px] uppercase tracking-widest font-black px-2 h-7 transition-colors"
                  data-testid={`library-quicklog-${p.id}`}
                  title="Log this peptide now"
                  aria-label={`Log ${p.name} now`}
                >
                  <Zap className="w-3 h-3" /> Log
                </button>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1.5 text-slate-600">
                  <Beaker className="w-3 h-3 text-[#0D9488]" />
                  <span className="font-mono-num">
                    {p.default_dose != null ? `${p.default_dose} ${p.default_unit || ""}` : "—"}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-slate-600">
                  <Clock className="w-3 h-3 text-[#0D9488]" />
                  <span className="font-mono-num">{p.default_interval_hours ?? 24}h</span>
                </div>
              </div>
              {p.description && (
                <p className="text-xs text-slate-500 mt-3 line-clamp-2 leading-relaxed">{p.description}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Peptide detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-white border border-slate-200 text-slate-900 rounded-none max-w-lg max-h-[92vh] overflow-y-auto" data-testid="peptide-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-[#0D9488] mb-1">
                  // {selected.category || "Peptide"}
                </div>
                <DialogTitle className="font-display uppercase tracking-tight text-3xl font-black" data-testid="peptide-detail-name">
                  {selected.name}
                </DialogTitle>
              </DialogHeader>

              <Tabs defaultValue="details" className="mt-3">
                <TabsList className="bg-slate-50 border border-slate-200 rounded-none h-10 p-0 w-full grid grid-cols-2 gap-0">
                  <TabsTrigger
                    value="details"
                    className="rounded-none h-full font-mono-num text-[11px] uppercase tracking-[0.25em] text-slate-600 data-[state=active]:bg-[#0D9488] data-[state=active]:text-black data-[state=active]:shadow-none"
                    data-testid="peptide-tab-details"
                  >
                    Details
                  </TabsTrigger>
                  <TabsTrigger
                    value="reconstitution"
                    className="rounded-none h-full font-mono-num text-[11px] uppercase tracking-[0.25em] text-slate-600 data-[state=active]:bg-[#0D9488] data-[state=active]:text-black data-[state=active]:shadow-none"
                    data-testid="peptide-tab-reconstitution"
                  >
                    Reconstitution
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="mt-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="border border-slate-200 bg-slate-50 p-3">
                      <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-slate-500 flex items-center gap-1 mb-1">
                        <Beaker className="w-3 h-3 text-[#0D9488]" /> Default Dose
                      </div>
                      <div className="font-mono-num text-lg text-slate-900">
                        {selected.default_dose != null ? `${selected.default_dose} ${selected.default_unit || ""}` : "Not set"}
                      </div>
                    </div>
                    <div className="border border-slate-200 bg-slate-50 p-3">
                      <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-slate-500 flex items-center gap-1 mb-1">
                        <Clock className="w-3 h-3 text-[#0D9488]" /> Interval
                        <InfoTip text="How often you take a dose, in hours between shots. A 168-hour interval = once weekly." />
                      </div>
                      <div className="font-mono-num text-lg text-slate-900">
                        {selected.default_interval_hours ?? 24}h
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">// Description</div>
                    <div className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap space-y-1">
                      {selected.description
                        ? selected.description.split(/(\*\*[^*]+\*\*)/g).map((chunk, i) =>
                            chunk.startsWith("**") && chunk.endsWith("**") ? (
                              <span key={i} className="block mt-3 font-semibold text-[#0D9488] uppercase tracking-wide text-xs">
                                {chunk.slice(2, -2)}
                              </span>
                            ) : (
                              <span key={i}>{chunk}</span>
                            )
                          )
                        : <span className="text-slate-400 italic">No description available.</span>
                      }
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="reconstitution" className="mt-4">
                  <ReconstitutionGuide peptide={selected} />
                </TabsContent>
              </Tabs>

              <DialogFooter className="pt-4 mt-2 border-t border-slate-200 flex flex-wrap gap-2 sm:justify-between">
                <Button
                  onClick={() => openQuickLog(selected)}
                  className="bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none uppercase tracking-widest font-semibold"
                  data-testid="peptide-detail-log-shot"
                >
                  <Syringe className="w-3.5 h-3.5 mr-2" /> Log This Peptide
                </Button>
                {isAdmin && (
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setConfirmDelete(selected)}
                      className="border-slate-200 bg-transparent hover:bg-slate-50 rounded-none text-slate-600 hover:text-[#FF3B30]"
                      data-testid="peptide-detail-delete">
                      <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                    </Button>
                    <Button onClick={() => openEdit(selected)} variant="outline"
                      className="border-slate-200 bg-transparent hover:bg-slate-50 rounded-none text-slate-800"
                      data-testid="peptide-detail-edit">
                      <Pencil className="w-3.5 h-3.5 mr-2" /> Edit
                    </Button>
                  </div>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick Log Shot dialog (pre-filled from selected peptide) */}
      <LogShotDialog
        open={logDialogOpen}
        onOpenChange={(v) => { setLogDialogOpen(v); if (!v) setQuickLogPeptide(null); }}
        peptides={peptides}
        initialPeptide={quickLogPeptide}
        shots={shots}
        onSaved={loadShots}
      />

      {/* Add / Edit dialog (admin only) */}
      {isAdmin && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-white border border-slate-200 text-slate-900 rounded-none max-w-lg" data-testid="peptide-dialog">
            <DialogHeader>
              <DialogTitle className="font-display uppercase tracking-tight text-3xl font-black">
                {editing ? "Edit Peptide" : "Add Peptide"}
              </DialogTitle>
              <DialogDescription className="font-mono-num text-[11px] uppercase tracking-[0.25em] text-slate-500">
                // {editing ? "update entry" : "new compound"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={onSubmit} className="space-y-4 mt-2" data-testid="peptide-form">
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Name</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-slate-50 border-slate-200 rounded-none h-11" data-testid="peptide-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Category</Label>
                  <Input list="library-categories-datalist" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="bg-slate-50 border-slate-200 rounded-none h-11" data-testid="peptide-category"
                    placeholder="e.g. Recovery & Repair" />
                  <datalist id="library-categories-datalist">
                    {categories.filter((c) => c !== ALL).map((c) => <option key={c} value={c} />)}
                  </datalist>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Interval (hrs)</Label>
                  <Input type="number" step="0.5" value={form.default_interval_hours}
                    onChange={(e) => setForm({ ...form, default_interval_hours: e.target.value })}
                    className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num" data-testid="peptide-interval" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2 space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Default Dose</Label>
                  <Input type="number" step="any" value={form.default_dose}
                    onChange={(e) => setForm({ ...form, default_dose: e.target.value })}
                    className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num" data-testid="peptide-dose" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Unit</Label>
                  <Select value={form.default_unit} onValueChange={(v) => setForm({ ...form, default_unit: v })}>
                    <SelectTrigger className="bg-slate-50 border-slate-200 rounded-none h-11" data-testid="peptide-unit"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 rounded-none">
                      <SelectItem value="mcg">mcg</SelectItem>
                      <SelectItem value="mg">mg</SelectItem>
                      <SelectItem value="IU">IU</SelectItem>
                      <SelectItem value="mL">mL</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Description</Label>
                <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="bg-slate-50 border-slate-200 rounded-none resize-none" data-testid="peptide-description" />
              </div>
              <div className="border border-slate-200 bg-slate-50/40 p-3 space-y-2">
                <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-[#0D9488]">// Suggested Reconstitution (optional)</div>
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  If set, this combo is highlighted as "Recommended by Admin" in the Reconstitution tab. Leave blank to auto-derive.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Vial Size (mg)</Label>
                    <Input type="number" step="any" value={form.suggested_vial_mg}
                      onChange={(e) => setForm({ ...form, suggested_vial_mg: e.target.value })}
                      placeholder="e.g. 10"
                      className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num" data-testid="peptide-suggested-vial-mg" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">BAC Water (mL)</Label>
                    <Input type="number" step="any" value={form.suggested_bac_water_ml}
                      onChange={(e) => setForm({ ...form, suggested_bac_water_ml: e.target.value })}
                      placeholder="e.g. 2"
                      className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num" data-testid="peptide-suggested-bac-ml" />
                  </div>
                </div>
              </div>
              <DialogFooter className="pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}
                  className="border-slate-200 bg-transparent hover:bg-slate-50 rounded-none text-slate-800"
                  data-testid="peptide-cancel">Cancel</Button>
                <Button type="submit" disabled={saving}
                  className="bg-[#0D9488] hover:bg-[#0F766E] text-black rounded-none uppercase tracking-widest font-semibold"
                  data-testid="peptide-save">
                  {saving ? "Saving…" : (editing ? "Update" : "Add Peptide")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-white border border-slate-200 rounded-none text-slate-900">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight">Delete peptide?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              This will remove "{confirmDelete?.name}" from the library for all users.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-slate-200 hover:bg-slate-50 rounded-none text-slate-800" data-testid="peptide-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-[#FF3B30] hover:bg-[#c9291f] rounded-none uppercase tracking-widest font-semibold" data-testid="peptide-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
